/**
 * scheduleIndex.ts
 * Frontend service for the Index Tests API (qa/index-tests-2026-07-23/INDEX-ACTION-PLAN.md).
 *
 * All requests go through apiClient (MSAL auth + error capture) — NEVER raw fetch().
 * Backend routes (see backend/src/app.ts):
 *   GET    /api/schedule/index-allocations?scenarioKey=…[&sourceType=&sourceKey=]
 *   POST   /api/schedule/index-allocations
 *   PATCH  /api/schedule/index-allocations/:key
 *   DELETE /api/schedule/index-allocations/:key
 *   GET    /api/schedule/index-allocations/:key/assignees
 *   POST   /api/schedule/index-allocations/:key/assignments
 *   DELETE /api/schedule/index-allocations/:key/assignments/:userKey
 *   GET    /api/schedule/index-capacity?from&to&scenarioKey
 *
 * Item 5.1 wired `listIndexAllocations` (Phase-3 tab enablement, I20).
 * Item 5.2 adds `patchIndexAllocation` (per-row window edit on the Phase-3
 * tab). Item 5.3 adds `getIndexCapacity` — the day-by-day shared-machine
 * utilization heatmap (I13) — mirroring the response shape built by
 * `backend/src/services/indexCapacityService.ts` (Item 3.2).
 *
 * Item 9.4 (People-phase staffing) adds `listIndexAllocationAssignees` /
 * `assignToIndexAllocation` / `unassignFromIndexAllocation` — mirrors the
 * task-assignment endpoints (`scheduleTasks.ts` / `scheduleAssignments.ts`) so
 * the People-phase "Index tests" section reuses the same skill-gate handling
 * (`SkillGateError`, re-exported from `scheduleAssignments.ts`).
 */

import apiClient from './apiClient';
import {
  SkillGateError,
  toScenarioLockedError,
  type SkillGateBlockedUser,
} from './scheduleAssignments';
import type { TaskAssignee } from './scheduleTasks';

// ── Types ────────────────────────────────────────────────────────────────────

export type IndexAllocationSourceType = 'Quote' | 'WorkOrder';
export type IndexAllocationStatus = 'Tentative' | 'Committed';

/** Frontend representation of an `IndexAllocation` row (backend + audit fields). */
export interface IndexAllocation {
  IndexAllocationKey: string;
  /** Scenario identifier. Defaults to 'BASELINE' on the backend. */
  ScenarioKey: string;
  SourceType: IndexAllocationSourceType;
  /** Set when SourceType='Quote'. Exactly one of QuoteKey/WorkOrderKey. */
  QuoteKey: string | null;
  /** Set when SourceType='WorkOrder'. Exactly one of QuoteKey/WorkOrderKey. */
  WorkOrderKey: string | null;
  /** FK → TaskType (IsIndexTest=true). One pooled row per index TaskType per source (I17). */
  TaskTypeKey: string;
  /** Σ of quote-line quantities pooled into this allocation (I3). */
  TestCount: number;
  /** Inclusive planning window start, 'YYYY-MM-DD'. */
  StartDate: string;
  /** Inclusive planning window end, 'YYYY-MM-DD'. */
  EndDate: string;
  Status: IndexAllocationStatus;
  /** Migration 118 (P1): 'index' = pooled index test; 'effort' = people-effort line (e.g. Lab Project Management). */
  Kind?: 'index' | 'effort';
  /**
   * Item 5.2: joined TaskType display name — only populated when
   * `listIndexAllocations` is called WITH a source filter (the repository
   * join lives in `indexAllocationRepository.findForSource`). `undefined`
   * when the list was scenario-wide (no source filter).
   */
  TaskTypeName?: string;
  /**
   * Item 5.2: joined `TaskType.IndexTestsPerDay` — same source-filtered-only
   * caveat as `TaskTypeName`. `null` means the admin hasn't set a capacity
   * yet (FE falls back to `INDEX_UNKNOWN_CAPACITY_FALLBACK`, mirroring the
   * backend); `undefined` means the list wasn't source-filtered.
   */
  IndexTestsPerDay?: number | null;
  // Audit fields
  sysCreateUser: string | null;
  sysCreateDate: string | null;
  sysLastModifiedUser: string | null;
  sysLastModifiedDate: string | null;
  sysLastAction: string | null;
}

/** Optional source filter for `listIndexAllocations`. */
export interface IndexAllocationSourceFilter {
  sourceType: IndexAllocationSourceType;
  sourceKey: string;
}

/** Body accepted by `PATCH /api/schedule/index-allocations/:key` (Item 5.2). */
export interface PatchIndexAllocationRequest {
  StartDate?: string;
  EndDate?: string;
  TestCount?: number;
}

// ── Service functions ─────────────────────────────────────────────────────────

/**
 * GET /api/schedule/index-allocations?scenarioKey=…[&sourceType=&sourceKey=]
 * Lists active (non-deleted) IndexAllocation rows for a scenario, optionally
 * narrowed to one source (Quote/WorkOrder). Used by the Phase-3 tab to decide
 * enablement (I20: enabled iff the source has ≥1 active allocation).
 */
export async function listIndexAllocations(
  scenarioKey: string,
  source?: IndexAllocationSourceFilter,
): Promise<IndexAllocation[]> {
  const params: Record<string, string> = { scenarioKey };
  if (source) {
    params.sourceType = source.sourceType;
    params.sourceKey = source.sourceKey;
  }
  const response = await apiClient.get<IndexAllocation[]>('/schedule/index-allocations', {
    params,
  });
  return response.data;
}

/**
 * PATCH /api/schedule/index-allocations/:key
 * Updates the planning window (StartDate/EndDate) and/or TestCount of an
 * existing allocation — used by the Phase-3 tab's per-row window pickers
 * (Item 5.2). At least one field must be supplied. Throws (axios error) on
 * 400 (invalid window / zero-workday) or 409 (scenario locked) — callers
 * surface `err.response.data.error` per the house pattern (see
 * useLockedPlanChoice.ts's `errorMessage` helper).
 */
export async function patchIndexAllocation(
  key: string,
  data: PatchIndexAllocationRequest,
): Promise<IndexAllocation> {
  const response = await apiClient.patch<IndexAllocation>(
    `/schedule/index-allocations/${key}`,
    data,
  );
  return response.data;
}

// ── Capacity (Item 5.3 — day-by-day shared-machine heatmap, I13) ──────────────

export type IndexCapacityBand = 'low' | 'mid' | 'high';

/**
 * One TaskType's contribution to a single day's index utilization.
 * P3 (Mike, 2026-07-29): each type carries its OWN %/band/over-capacity against
 * its OWN tests/day capacity — "each index test is treated like a machine".
 */
export interface IndexDayTypeBreakdown {
  taskTypeKey: string;
  taskTypeName: string;
  /** Σ firm-classified fractional per-day test counts for this type, this day. */
  firmCount: number;
  /** Σ soft-classified (other drafts') fractional per-day test counts for this type, this day. */
  softCount: number;
  /** TaskType.IndexTestsPerDay, or null when unset (falls back to the 8/day default). */
  capacity: number | null;
  /** This type's firm % of ITS OWN capacity — the authoritative per-type number (P3). */
  firmPct: number;
  /** This type's soft % of its own capacity — advisory only. */
  softPct: number;
  /** true when this type's capacity was unset and the fallback engaged. */
  hasUnknownCapacity: boolean;
  /** Band of THIS type's firmPct. */
  band: IndexCapacityBand;
  /** true iff THIS type's firmPct > 100 (I9 — warns, never blocks). */
  isOverCapacity: boolean;
}

/** A test type seen anywhere in the queried window — stable row roster for per-type grids (P3). */
export interface IndexCapacityTypeInfo {
  taskTypeKey: string;
  taskTypeName: string;
  capacity: number | null;
}

/** One calendar day's index utilization (I13 core deliverable; I15 daily granularity). */
export interface IndexDayCapacity {
  /** ISO date 'YYYY-MM-DD'. */
  date: string;
  /** false for weekends/stat holidays — the grid greys these cells rather than omitting them. */
  isWorkingDay: boolean;
  /**
   * Firm activity SUMMED across all types — informational only since P3
   * (2026-07-29): never derive warnings or colors from this; use the per-type
   * `byType[].firmPct` / `maxTypeFirmPct` instead.
   */
  firmPct: number;
  /** Soft activity summed across types — informational (see firmPct). */
  softPct: number;
  /** The busiest single type's firmPct — the day's honest load number (P3). */
  maxTypeFirmPct: number;
  /** The busiest single type's softPct — advisory. */
  maxTypeSoftPct: number;
  /** true if any type active this day used the unknown-capacity fallback. */
  hasUnknownCapacity: boolean;
  /** Band of the WORST single type (P3 — never the cross-type sum). */
  band: IndexCapacityBand;
  /** true iff ANY type is over ITS OWN capacity (P3 — never the cross-type sum). */
  isOverCapacity: boolean;
  byType: IndexDayTypeBreakdown[];
}

export interface IndexCapacityResult {
  from: string;
  to: string;
  /** The scenario perspective actually used ('BASELINE' when the query omitted scenarioKey). */
  scenarioKey: string;
  /** Every type active anywhere in [from, to], name-sorted — per-type grid row roster (P3). */
  types: IndexCapacityTypeInfo[];
  days: IndexDayCapacity[];
}

/**
 * GET /api/schedule/index-capacity?from&to&scenarioKey
 * Day-by-day utilization of the shared virtual Index machine across ALL
 * projects/scenarios (I13). `scenarioKey` sets the "own scenario" perspective
 * for the firm/soft split (own Tentative counts as firm; other drafts'
 * Tentative is soft/advisory) — omit it for the BASELINE perspective.
 */
export async function getIndexCapacity(
  from: string,
  to: string,
  scenarioKey?: string,
): Promise<IndexCapacityResult> {
  const params: Record<string, string> = { from, to };
  if (scenarioKey) params.scenarioKey = scenarioKey;
  const response = await apiClient.get<IndexCapacityResult>('/schedule/index-capacity', {
    params,
  });
  return response.data;
}

// ── Staffing (Item 9.4 — People-phase "Index tests" section, R2) ─────────────

/**
 * A person currently staffed on an IndexAllocation. Same wire shape as
 * `TaskAssignee` (backend `taskAssigneesController.ASSIGNEE_SELECT` pattern,
 * mirrored by `listIndexAllocationAssignees` in
 * `indexAllocationStaffingController.ts`) minus `MachineBookingKey` (index
 * allocations have no MachineBooking, A4) — reusing the type lets the section
 * share `TaskAssignee`-shaped chip rendering with the task grid.
 */
export type IndexAllocationAssignee = TaskAssignee;

/**
 * GET /api/schedule/index-allocations/:key/assignees
 * Currently-staffed people for one pooled allocation (one row per person·week
 * — dedupe by UserKey for chip display, same convention as listTaskAssignees).
 */
export async function listIndexAllocationAssignees(
  allocationKey: string,
): Promise<IndexAllocationAssignee[]> {
  const response = await apiClient.get<IndexAllocationAssignee[]>(
    `/schedule/index-allocations/${allocationKey}/assignees`,
  );
  return response.data;
}

/** Result of a successful (non-blocked) assign. */
export interface AssignToIndexAllocationResult {
  /** The created per-week presence rows. */
  assignments: unknown[];
}

/**
 * POST /api/schedule/index-allocations/:key/assignments
 * Stage a person onto a pooled allocation — one presence row per week
 * spanning the allocation's CURRENT window (server-computed).
 *
 * @throws ScenarioLockedError  the scenario is Submitted/Archived (409).
 * @throws SkillGateError       the advisory mentor gate blocked this user
 *                              (409 skill_gate) — retry with confirmSkillGate.
 */
export async function assignToIndexAllocation(
  allocationKey: string,
  userKey: string,
  confirmSkillGate = false,
): Promise<AssignToIndexAllocationResult> {
  try {
    const response = await apiClient.post<{ assignments: unknown[] }>(
      `/schedule/index-allocations/${allocationKey}/assignments`,
      { userKey, ...(confirmSkillGate ? { confirmSkillGate: true } : {}) },
    );
    return response.data;
  } catch (err: unknown) {
    const locked = toScenarioLockedError(err);
    if (locked) throw locked;
    const axiosErr = err as { response?: { status?: number; data?: Record<string, unknown> } };
    if (axiosErr?.response?.status === 409 && axiosErr.response.data?.error === 'skill_gate') {
      const d = axiosErr.response.data as {
        message: string;
        blockedUsers: SkillGateBlockedUser[];
        taskTypeKey: string | null;
      };
      throw new SkillGateError(d.message, d.blockedUsers, d.taskTypeKey);
    }
    throw err;
  }
}

/**
 * DELETE /api/schedule/index-allocations/:key/assignments/:userKey
 * Unassign a person — soft-deletes every active presence row they hold on
 * this allocation. Returns the number of rows removed (0 = idempotent no-op).
 *
 * @throws ScenarioLockedError  the scenario is Submitted/Archived (409).
 */
export async function unassignFromIndexAllocation(
  allocationKey: string,
  userKey: string,
): Promise<number> {
  try {
    const response = await apiClient.delete<{ removed: number }>(
      `/schedule/index-allocations/${allocationKey}/assignments/${userKey}`,
    );
    return response.data.removed;
  } catch (err: unknown) {
    const locked = toScenarioLockedError(err);
    if (locked) throw locked;
    throw err;
  }
}
